import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ContactHero = () => {
  const floatingElements = [
    { icon: 'MessageCircle', delay: 0, x: 20, y: -30 },
    { icon: 'Phone', delay: 0.2, x: -40, y: 20 },
    { icon: 'Mail', delay: 0.4, x: 60, y: 40 },
    { icon: 'MapPin', delay: 0.6, x: -20, y: -50 },
    { icon: 'Calendar', delay: 0.8, x: 80, y: -20 },
    { icon: 'Users', delay: 1.0, x: -60, y: 60 }
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-primary/5 to-accent/10">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 perspective-1000">
        {floatingElements?.map((element, index) => (
          <motion.div
            key={index}
            className="absolute"
            style={{
              left: `${20 + (index * 15)}%`,
              top: `${30 + (index * 10)}%`
            }}
            initial={{ opacity: 0, scale: 0, rotateY: -180 }}
            animate={{ 
              opacity: 0.1, 
              scale: 1, 
              rotateY: 0,
              x: element?.x,
              y: element?.y
            }}
            transition={{ 
              delay: element?.delay,
              duration: 1.5,
              repeat: Infinity,
              repeatType: "reverse",
              repeatDelay: 2
            }}
          >
            <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <Icon name={element?.icon} size={24} className="text-accent" />
            </div>
          </motion.div>
        ))}
      </div>
      {/* Main Content */}
      <div className="relative z-10 text-center max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 50, rotateX: -30 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="transform-3d"
        >
          <motion.div
            className="inline-flex items-center space-x-2 bg-accent/10 backdrop-blur-sm rounded-full px-6 py-3 mb-8 border border-accent/20"
            whileHover={{ scale: 1.05, rotateY: 5 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <Icon name="Zap" size={20} className="text-accent" />
            <span className="text-accent font-medium">Contact Command Center</span>
          </motion.div>

          <motion.h1 
            className="text-5xl md:text-7xl font-bold text-foreground mb-6 text-shadow-3d"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Let's Build Something
            <motion.span 
              className="block text-accent"
              animate={{ 
                textShadow: [
                  "0 0 20px rgba(0, 212, 255, 0.5)",
                  "0 0 40px rgba(0, 212, 255, 0.8)",
                  "0 0 20px rgba(0, 212, 255, 0.5)"
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Extraordinary
            </motion.span>
          </motion.h1>

          <motion.p 
            className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            Ready to transform your vision into reality? Our team is standing by to discuss your project, 
            provide expert consultation, and craft the perfect digital solution for your needs.
          </motion.p>

          <motion.div 
            className="flex flex-col sm:flex-row items-center justify-center gap-6"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
          >
            <motion.div
              className="flex items-center space-x-2 text-success"
              whileHover={{ scale: 1.05 }}
            >
              <div className="w-3 h-3 bg-success rounded-full animate-pulse"></div>
              <span className="font-medium">Available Now</span>
            </motion.div>
            
            <motion.div
              className="flex items-center space-x-2 text-muted-foreground"
              whileHover={{ scale: 1.05 }}
            >
              <Icon name="Clock" size={16} />
              <span>Response within 2 hours</span>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-accent/50 rounded-full flex justify-center">
          <motion.div
            className="w-1 h-3 bg-accent rounded-full mt-2"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default ContactHero;